import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';

const emptyForm = {
  name: '',
  country: 'Portugal',
  description: '',
  lat: '',
  lng: '',
  radius: 5000,
  imageUrl: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

export default function AdminCitiesPage() {
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminService.cities.list({ limit: 100 });
      setCities(res.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar as cidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setNotice('');
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const editCity = (city) => {
    setEditingId(city.id);
    setForm({
      name: city.name || '',
      country: city.country || 'Portugal',
      description: city.description || '',
      lat: city.lat || '',
      lng: city.lng || '',
      radius: city.radius ?? 5000,
      imageUrl: city.imageUrl || '',
      isActive: Boolean(city.isActive),
    });
    setModalOpen(true);
  };

  const saveCity = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    const payload = {
      ...form,
      lat: Number(form.lat),
      lng: Number(form.lng),
      radius: Number(form.radius),
    };

    try {
      if (editingId) {
        await adminService.cities.update(editingId, payload);
        setNotice('Cidade atualizada com sucesso.');
      } else {
        await adminService.cities.create(payload);
        setNotice('Cidade criada com sucesso.');
      }
      await loadData();
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar a cidade.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (city) => {
    try {
      await adminService.cities.update(city.id, { isActive: !city.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado da cidade.');
    }
  };

  const removeCity = async (city) => {
    if (!window.confirm(`Remover a cidade "${city.name}"?`)) return;
    try {
      await adminService.cities.remove(city.id);
      await loadData();
    } catch {
      setError('Não foi possível remover a cidade.');
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Território</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Cidades</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">Controla as cidades disponíveis no jogo e o raio operacional de cada uma.</p>
        </div>
        <button type="button" onClick={openCreateModal} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Nova cidade
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex items-center justify-between border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Cidades registadas</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">{loading ? 'A carregar...' : `${cities.length} cidade(s)`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">País</th>
                <th className="px-4 py-3">Coordenadas</th>
                <th className="px-4 py-3">Raio</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && cities.length === 0 && <tr><td className="px-4 py-6 text-fg/45" colSpan="6">Nenhuma cidade registada.</td></tr>}
              {cities.map((city) => (
                <tr key={city.id}>
                  <td className="px-4 py-3"><div className="font-bold text-fg">{city.name}</div><div className="text-xs text-fg/40">{city.description || 'Sem descrição'}</div></td>
                  <td className="px-4 py-3 text-fg/60">{city.country}</td>
                  <td className="px-4 py-3 text-fg/60">{city.lat}, {city.lng}</td>
                  <td className="px-4 py-3 text-fg/60">{city.radius}m</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggleActive(city)} className={`rounded-full border px-3 py-1 text-xs font-bold ${city.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>
                      {city.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editCity(city)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">Editar</button>
                    <button type="button" onClick={() => removeCity(city)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Território</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar cidade' : 'Nova cidade'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">Fechar</button>
          </div>
          <form className="mt-5 grid gap-3" onSubmit={saveCity}>
            <label className="grid gap-1"><span className={labelClass}>Nome</span><input className={fieldClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
            <label className="grid gap-1"><span className={labelClass}>País</span><input className={fieldClass} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required /></label>
            <label className="grid gap-1"><span className={labelClass}>Descrição</span><textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1"><span className={labelClass}>Latitude</span><input className={fieldClass} type="number" step="0.0000001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} required /></label>
              <label className="grid gap-1"><span className={labelClass}>Longitude</span><input className={fieldClass} type="number" step="0.0000001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} required /></label>
            </div>
            <label className="grid gap-1"><span className={labelClass}>Raio da cidade</span><input className={fieldClass} type="number" min="100" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} /></label>
            <label className="grid gap-1"><span className={labelClass}>Imagem URL</span><input className={fieldClass} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} /></label>
            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />Ativa</label>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">{saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar cidade'}</button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
